import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import AsyncLoader from '~components/AsyncLoader';
import EmptyState from '~components/EmptyState';
import {
  deletePredictiveIvrMessage,
  getPredictiveIvrMessages,
  getS3AudioFileFromUrl,
  upsertPredictiveIvrMessage,
} from '~pages/SipConfiguration/api';
import { PredictiveIvrMessage } from '~pages/SipConfiguration/domain';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import CreateEditIVRMessageModal from './CreateIVRMessageModal';
import IvrMessageCard from './IVRMessageCard';

interface Query {
  search: string;
}

interface Error {
  text: string;
  subText: string;
}

const IVRMessageList = () => {
  const { pushNotification } = useNotification();
  const [pageLoaded, setPageLoaded] = useState<boolean>(false);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editableIvrMessage, setEditableIvrMessage] = useState<PredictiveIvrMessage | null>(null);
  const [submittingData, setSubmittingData] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [ivrMessages, setIvrMessages] = useState<PredictiveIvrMessage[]>([]);
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const [query, setQuery] = useState<Query>({
    search: '',
  });

  useEffect(() => {
    (async () => {
      try {
        setIvrMessages(await getPredictiveIvrMessages());
      } catch (e) {
        handleError(e);
      } finally {
        setPageLoaded(true);
      }
    })();
  }, []);

  const handleError = (e: any) => {
    if (e instanceof APIError) {
      setError({ text: 'Unable to request data from backend', subText: e.message });
    }

    if (e instanceof UnsupportedStructureError) {
      setError({ text: 'Data from backend Invalid', subText: 'Unable to decode response' });
    }
  };

  const handlePlayAudio = async (item: PredictiveIvrMessage | null) => {
    if (item && item.audioLocation && item.signedUrl) {
      const { audioLocation, key, signedUrl } = item;
      let audioBuffer = null;
      if (!audioLocation || !signedUrl) return;

      if (!audioContextRef.current) {
        console.log(`New Audio Context`);
        audioContextRef.current = new AudioContext();
      }

      if (!signedUrl) {
        throw new Error('Signed URL is undefined');
      }
      const response = await getS3AudioFileFromUrl(signedUrl);
      const arrayBuffer = await response.arrayBuffer();

      if (audioLocation.endsWith('.sln16')) {
        const pcmData = new Int16Array(arrayBuffer);
        audioBuffer = audioContextRef.current.createBuffer(1, pcmData.length, 16000);
        const channelData = audioBuffer.getChannelData(0);
        for (const [index, sample] of pcmData.entries()) {
          channelData[index] = sample / 32768; // 16-bit normalization
        }
      } else if (audioLocation.endsWith('.wav')) {
        audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      }
      audioBufferRef.current = audioBuffer;
      setPlayingMessageId(key);
    }
  };

  useEffect(() => {
    const playAudio = async () => {
      if (playingMessageId !== null && audioContextRef.current && audioBufferRef.current) {
        sourceRef.current = audioContextRef.current.createBufferSource();
        sourceRef.current.buffer = audioBufferRef.current;
        sourceRef.current.connect(audioContextRef.current.destination);
        sourceRef.current.onended = () => {
          if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
          }
          sourceRef.current = null;
          setPlayingMessageId(null);
        };
        sourceRef.current.start();
      }
    };
    if (playingMessageId !== null) {
      if (sourceRef.current) {
        sourceRef.current.stop();
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      playAudio();
    } else {
      if (sourceRef.current) {
        sourceRef.current.stop();
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
    }

    return () => {
      if (sourceRef.current) {
        sourceRef.current.stop();
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
    };
  }, [playingMessageId]);

  const handleStopAudio = () => {
    setPlayingMessageId(null);
  };

  const openCreateModal = () => {
    setCreateModalOpen(true);
  };

  const openEditModal = (item: PredictiveIvrMessage) => () => {
    setEditableIvrMessage(item);
  };

  const closeModal = () => {
    setCreateModalOpen(false);
    setEditableIvrMessage(null);
  };

  const onQueryChange = (e: ChangeEvent<any>) => {
    const { name, value } = e.target;
    setQuery((prev) => ({ ...prev, [name]: value }));
  };

  const upsertMessage = async (isEdit: boolean, msg: PredictiveIvrMessage) => {
    setSubmittingData(true);

    try {
      await upsertPredictiveIvrMessage(isEdit, msg);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);

      // Modal catches error to prevent form reset on create failure
      return Promise.reject();
    } finally {
      setSubmittingData(false);
    }

    pushNotification('success', `Created predictive IVR message ${msg.key}`);

    try {
      setIvrMessages(await getPredictiveIvrMessages());
    } catch (e) {
      handleError(e);
    } finally {
      closeModal();
    }
  };

  const deleteMessage = (key: string) => async () => {
    setSubmittingData(true);

    try {
      await deletePredictiveIvrMessage(key);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);

      // Modal catches error to prevent form reset on create failure
      return Promise.reject();
    } finally {
      setSubmittingData(false);
    }

    pushNotification('success', `Deleted predictive IVR message ${key}`);

    try {
      setIvrMessages(await getPredictiveIvrMessages());
    } catch (e) {
      handleError(e);
    } finally {
      closeModal();
    }
  };

  const displayList = ivrMessages
    // Search Fuzzy matching
    .filter((item: PredictiveIvrMessage) => {
      if (query.search) {
        const searchQuery = query.search.toLowerCase();
        return item.key.toLowerCase().includes(searchQuery);
      }

      return true;
    })
    // Component generation
    .map((item: PredictiveIvrMessage, index: number) => (
      <IvrMessageCard
        key={index}
        ivrMessage={item}
        isPlaying={playingMessageId === item.key}
        onPlayAudio={() => handlePlayAudio(item)}
        onStopAudio={() => handleStopAudio()}
        onEdit={openEditModal(item)}
        onDelete={deleteMessage(item.key)}
      />
    ));

  // If any filter property is set and displayList is empty we assume no relevant search results
  const noSearchOrFilterResults = query.search && displayList.length === 0;
  const noResults = ivrMessages.length === 0;

  const errorDisplay = error ? <EmptyState type='error' text={error.text} subText={error.subText} /> : null;

  return (
    <AsyncLoader isLoading={!pageLoaded} error={errorDisplay}>
      {noResults && (
        <EmptyState
          type='no-items-3'
          text='No predictive IVR messages currently exist'
          subText='Create a  predictive IVR message by clicking the button below'
          action={openCreateModal}
          actionText='Create IVR Message'
        />
      )}
      {!noResults && (
        <Grid
          container
          spacing={1}
          sx={{
            alignContent: 'center',
          }}>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}>
            <TextField
              fullWidth
              variant='outlined'
              label='Search'
              id='search'
              name='search'
              defaultValue={query.search}
              onChange={onQueryChange}
            />
          </Grid>

          <Grid
            size={{
              md: 5,
            }}
            sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>

          <Grid
            style={{ display: 'flex', alignItems: 'center' }}
            size={{
              xs: 12,
              md: 3,
            }}>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              fullWidth
              startIcon={<AddIcon />}
              onClick={openCreateModal}>
              Create IVR Message
            </Button>
          </Grid>

          <Grid size={12}>
            {!noSearchOrFilterResults && <List>{displayList}</List>}
            {noSearchOrFilterResults && (
              <EmptyState
                type='no-records-found'
                text='No predictive IVR messages found matching your search criteria'
                subText='Try alternate words or selections.'
              />
            )}
          </Grid>
        </Grid>
      )}
      <CreateEditIVRMessageModal
        open={createModalOpen || Boolean(editableIvrMessage)}
        submitting={submittingData}
        ivrMessage={editableIvrMessage}
        onClose={closeModal}
        onAccept={upsertMessage}
      />
    </AsyncLoader>
  );
};

export default IVRMessageList;
